import { DateInput } from '@mantine/dates'
import { useCallback, useMemo, useRef, useState } from 'react'
import { Modal, Select, Checkbox, Text, Tooltip } from '@mantine/core'
import { USDateInput } from '@/components/ui/USDateInput'
import { IconX, IconPlus, IconInfoTriangleFilled } from '@tabler/icons-react'
import type { Draft, DayKey } from '../../service'
import {
  buildTimeOptions,
  getNextTimeSlotEnding,
  isMultipleDayRange,
  isTimePast,
  isEarlier,
} from '../../utils'

type TabValue = 'available' | 'block'

type Props = {
  opened: boolean
  onClose: () => void
  setAvailabilitySlots?: (data: { scheduleDraft: Draft; endsOn: Date | null }) => void
  setBlockedSlots?: (
    blockPeriods: { startDate: Date; endDate: Date; startTime: string; endTime: string }[],
  ) => void
}

// Segmented tab pill — mirrors the Schedule page Day/Week/Month view selector
// (CalendarViewSwitcher): coral-filled active tab, full-width segments.
const tabBase =
  'flex-1 cursor-pointer rounded-md border-none px-4 py-[1.5px] !text-[16px] leading-none !font-semibold transition-all ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
const tabInactive = 'bg-transparent font-semibold text-[#6B7280]'
const tabActive = 'bg-[#E5533D] font-bold text-white shadow-[0_1px_2px_rgba(203,71,31,.3)]'

// Footer buttons — smaller/bolder Mulish, coral primary, matching the other
// Schedule v2 modals.
const cancelBtn =
  'schedule-v2-mulish rounded-md border border-[#D8DBE0] bg-white px-4 py-[5px] !text-[12px] !font-bold text-[#33383F] hover:bg-[#F4F6F8]'
const saveBtn = (disabled: boolean) =>
  `schedule-v2-mulish rounded-md px-4 py-[5px] !text-[12px] !font-bold ${
    disabled
      ? 'cursor-not-allowed bg-[#F1F3F6] text-[#B0B6BE]'
      : 'bg-[#E5533D] text-white shadow-[0_1px_2px_rgba(203,71,31,0.3)] hover:bg-[#D4472F]'
  }`

const timeInputClass = 'h-[42px] rounded-lg border border-[#D8DBE0] text-sm font-semibold'

function DayPill({ label, active }: { label: string; active?: boolean }) {
  return (
    <div
      className={[
        'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold',
        active
          ? 'border border-[#E5533D] bg-[#FDECE8] text-[#E5533D]'
          : 'border border-[#D8DBE0] text-[#6B7280]',
      ].join(' ')}
    >
      {label}
    </div>
  )
}

function IconBtn({
  children,
  title,
  onClick,
}: {
  children: React.ReactNode
  title: string
  onClick: () => void
}) {
  return (
    <button
      title={title}
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#D8DBE0] text-[#8A929C] transition-colors hover:bg-[#F2F4F6] hover:text-[#1F2733]"
      type="button"
      onClick={onClick}
    >
      {children}
    </button>
  )
}
const DEFAULT_SCHEDULE_DRAFT = {
  Mon: { start: '09:00', end: '17:00' },
  Tue: { start: '09:00', end: '17:00' },
  Wed: { start: '09:00', end: '17:00' },
  Thu: { start: '09:00', end: '17:00' },
  Fri: { start: '09:00', end: '17:00' },
}
const DEFAULT_DRAFT_SCHEDULE_ENDS_ON = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)
export default function AvailabilityModal({
  opened,
  onClose,
  setAvailabilitySlots,
  setBlockedSlots,
}: Props) {
  const timeOptions = useMemo(() => buildTimeOptions(8, 20, 30), [])
  const defaultOption =
    timeOptions.filter((option) => !isTimePast(option.label))[0] ?? timeOptions[0] // fall back to first slot if all are past (e.g. after 8 PM)
  const [endsOn, setEndsOn] = useState<Date | null>(DEFAULT_DRAFT_SCHEDULE_ENDS_ON)
  const [endsChecked, setEndsChecked] = useState(false)
  const [scheduleDraft, setScheduleDraft] = useState<Draft>(DEFAULT_SCHEDULE_DRAFT as Draft)
  const blockIdCounter = useRef(1)
  const [blockPeriods, setBlockPeriods] = useState<
    { id: string; startDate: Date; endDate: Date; startTime: string; endTime: string }[]
  >([
    {
      id: 'block-0',
      startDate: new Date(),
      endDate: new Date(),
      startTime: defaultOption?.value,
      endTime: getNextTimeSlotEnding(defaultOption?.value),
    },
  ])
  const [invalidBlockKeys, setInvalidBlockKeys] = useState<Set<string>>(new Set())
  const [activeTab, setActiveTab] = useState<TabValue>('available')

  const setBlockFieldValidity = useCallback((key: string, valid: boolean) => {
    setInvalidBlockKeys((prev) => {
      if (valid === !prev.has(key)) return prev
      const next = new Set(prev)
      if (valid) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const handleSave = () => {
    if (activeTab === 'available') {
      if (setAvailabilitySlots) {
        setAvailabilitySlots({ scheduleDraft, endsOn })
        setScheduleDraft(DEFAULT_SCHEDULE_DRAFT as Draft) // Reset draft after saving
      }
    } else {
      setBlockedSlots?.(blockPeriods.map(({ id: _id, ...rest }) => rest))
    }
    onClose()
  }
  const addNewPeriod = () => {
    setBlockPeriods((prev) => [
      ...prev,
      {
        id: `block-${blockIdCounter.current++}`,
        startDate: new Date(),
        endDate: new Date(),
        startTime: defaultOption.value,
        endTime: (timeOptions.filter((option) => !isTimePast(option.label))[1] ?? timeOptions[1])
          .value,
      },
    ])
  }
  const removePeriod = (index: number) => {
    const removed = blockPeriods[index]
    setBlockPeriods((prev) => prev.filter((_, i) => i !== index))
    setInvalidBlockKeys((prev) => {
      if (!prev.has(`${removed.id}-start`) && !prev.has(`${removed.id}-end`)) return prev
      const next = new Set(prev)
      next.delete(`${removed.id}-start`)
      next.delete(`${removed.id}-end`)
      return next
    })
  }
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      withCloseButton={false}
      classNames={{
        content: 'schedule-v2-scope rounded-[18px] shadow-[0_24px_60px_rgba(16,24,40,0.28)]',
        body: 'p-[22px]',
      }}
      radius={18}
      size="700px"
    >
      {/* Header */}
      <div className="mb-5 flex items-start gap-2.5">
        <div className="flex-1">
          <div className="text-[17px] font-extrabold tracking-tight text-[#1F2733]">
            Set your availability
          </div>
          <div className="text-[12.5px] text-[#8A929C]">
            Set when you are available for meetings
          </div>
        </div>
        <button
          type="button"
          title="Close"
          onClick={onClose}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[7px] text-[#8A929C] hover:bg-[#F2F4F6] hover:text-[#1F2733]"
        >
          <IconX size={17} stroke={2.1} />
        </button>
      </div>

      {/* Full-width segmented tabs — matches the Day/Week/Month view selector */}
      <div
        role="radiogroup"
        aria-label="Availability type"
        className="schedule-v2-mulish mb-5 flex w-full items-center gap-0.5 rounded-lg bg-[#F1F2F4] p-1"
      >
        <button
          type="button"
          role="radio"
          aria-checked={activeTab === 'available'}
          onClick={() => setActiveTab('available')}
          className={`${tabBase} ${activeTab === 'available' ? tabActive : tabInactive}`}
        >
          Available
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={activeTab === 'block'}
          onClick={() => setActiveTab('block')}
          className={`${tabBase} ${activeTab === 'block' ? tabActive : tabInactive}`}
        >
          Block
        </button>
      </div>

      {/* Available tab */}
      {activeTab === 'available' && (
        <div>
          {/* Weekdays rows */}
          {(['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as DayKey[]).map((d) => (
            <div key={`${d}-day-row`} className="flex items-center gap-3 py-1">
              {Object.keys(scheduleDraft).includes(d) ? (
                <>
                  <DayPill label={d} active />
                  <div className="flex flex-1 items-center gap-2">
                    <Select
                      name={`${d}-start`}
                      data={[...timeOptions].slice(0, -1)}
                      defaultValue={'09:00'}
                      classNames={{ root: 'flex-1', input: timeInputClass }}
                      comboboxProps={{ withinPortal: true }}
                      allowDeselect={false}
                      onChange={(value) => {
                        setScheduleDraft((prev) => ({
                          ...prev,
                          [d]: {
                            ...prev[d],
                            start: value || '09:00',
                            end:
                              value && prev[d]?.end && prev[d].end <= value
                                ? getNextTimeSlotEnding(value)
                                : prev[d]!.end,
                          },
                        }))
                      }}
                    />
                    <Text className="mx-1 text-[#9AA0A8]">-</Text>
                    <Select
                      name={`${d}-end`}
                      data={timeOptions.map((opt) => ({
                        ...opt,
                        disabled: opt.value <= scheduleDraft[d]!.start,
                      }))}
                      value={scheduleDraft[d]!.end}
                      classNames={{ root: 'flex-1', input: timeInputClass }}
                      comboboxProps={{ withinPortal: true }}
                      allowDeselect={false}
                      onChange={(value) => {
                        setScheduleDraft((prev) => ({
                          ...prev,
                          [d]: { ...prev[d], end: value || '17:00' },
                        }))
                      }}
                    />
                    <IconBtn
                      title="Remove"
                      onClick={() => {
                        setScheduleDraft((prev) => {
                          const newDraft = { ...prev }
                          delete newDraft[d]
                          return newDraft
                        })
                      }}
                    >
                      <IconX size={15} stroke={2} />
                    </IconBtn>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-3">
                  <DayPill label={d} />
                  <button
                    onClick={() => {
                      setScheduleDraft((prev) => ({
                        ...prev,
                        [d]: { start: '09:00', end: '17:00' },
                      }))
                    }}
                    className="schedule-v2-mulish rounded-md px-2 py-1 text-[13px] font-bold text-[#E5533D] hover:bg-[#FDECE8]"
                    type="button"
                  >
                    Add +
                  </button>
                </div>
              )}
            </div>
          ))}
          {/* Ends */}
          <div className="mt-4 space-y-2">
            <Checkbox
              color="#E5533D"
              classNames={{
                input: 'checked:!border-[#E5533D] checked:!bg-[#E5533D]',
              }}
              label="Set end for this schedule manually"
              checked={endsChecked}
              onChange={(e) => {
                setEndsChecked(e.currentTarget.checked)
                setEndsOn(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)) // 2 weeks
              }}
            />
            <div className="pl-6">
              {endsChecked ? (
                <USDateInput
                  value={endsOn}
                  onChange={(value) => {
                    if (!value) {
                      setEndsOn(null)
                      return
                    }
                    const [year, month, day] = value.split('-').map(Number)
                    setEndsOn(new Date(year, month - 1, day))
                  }}
                  minDate={new Date()}
                  maxDate={new Date(Date.now() + 2 * 30 * 24 * 60 * 60 * 1000)}
                  classNames={{
                    input: `${timeInputClass} disabled:bg-gray-100 disabled:text-gray-400`,
                  }}
                />
              ) : (
                <div className="mb-1 text-xs text-[#8A929C]">
                  Once saved, this schedule will take effect for two weeks, <br /> and end on{' '}
                  {new Intl.DateTimeFormat('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  }).format(Date.now() + 14 * 24 * 60 * 60 * 1000)}
                </div>
              )}
            </div>
          </div>
          {/* Footer */}
          <div className="mt-6 flex items-center justify-end gap-2.5 border-t border-[#EEF0F3] pt-[18px]">
            <button type="button" onClick={onClose} className={cancelBtn}>
              Cancel
            </button>
            <button
              type="button"
              disabled={Object.keys(scheduleDraft).length === 0 || (endsChecked && !endsOn)}
              onClick={handleSave}
              className={saveBtn(
                Object.keys(scheduleDraft).length === 0 || (endsChecked && !endsOn),
              )}
            >
              Save
            </button>
          </div>
        </div>
      )}

      {/* Block tab */}
      {activeTab === 'block' && (
        <div>
          {blockPeriods.map((period, index) => (
            <div key={period.id} className="mb-4 flex items-start gap-x-[10px]">
              <USDateInput
                key={`${period.id}-start`}
                value={period.startDate}
                onChange={(d) =>
                  d &&
                  setBlockPeriods((prev) => {
                    const [year, month, day] = d.split('-').map(Number)
                    const date = new Date(year, month - 1, day)
                    const newPeriods = [...prev]
                    newPeriods[index] = { ...newPeriods[index], startDate: date }
                    if (newPeriods[index].endDate < newPeriods[index].startDate) {
                      newPeriods[index].endDate = date
                    }
                    return newPeriods
                  })
                }
                onValidityChange={(valid) => setBlockFieldValidity(`${period.id}-start`, valid)}
                minDate={new Date()}
                aria-label="Start date"
                classNames={{ input: `${timeInputClass} max-w-[180px]` }}
              />
              <Text className="text-[#9AA0A8]">-</Text>
              <USDateInput
                key={`${period.id}-end`}
                value={period.endDate}
                defaultDate={blockPeriods[index].startDate}
                onChange={(d) =>
                  d &&
                  setBlockPeriods((prev) => {
                    const [year, month, day] = d.split('-').map(Number)
                    const date = new Date(year, month - 1, day)
                    const newPeriods = [...prev]
                    newPeriods[index] = { ...newPeriods[index], endDate: date }
                    return newPeriods
                  })
                }
                onValidityChange={(valid) => setBlockFieldValidity(`${period.id}-end`, valid)}
                minDate={blockPeriods[index].startDate}
                valueFormat="MM/DD/YY"
                classNames={{ input: `${timeInputClass} max-w-[92px]` }}
                classNames={{ input: `${timeInputClass} max-w-[180px]` }}
                aria-label="End date"
              />
              <Select
                name={`block-start-${index}`}
                withCheckIcon={false}
                data={[...timeOptions].slice(0, -1).map((opt) => ({
                  ...opt,
                  disabled:
                    isTimePast(opt.value) &&
                    period.startDate.toDateString() === new Date().toDateString(),
                }))}
                value={period.startTime}
                size="sm"
                classNames={{
                  input: `${timeInputClass} max-w-[96px] text-center !p-2`,
                  option: '!text-[13px] py-[0px] text-center ',
                }}
                rightSectionWidth={0}
                comboboxProps={{ withinPortal: true }}
                allowDeselect={false}
                onChange={(value) => {
                  const endTime =
                    isEarlier(blockPeriods[index].endTime, value as string) ||
                    blockPeriods[index].endTime === value
                      ? getNextTimeSlotEnding(value || '')
                      : blockPeriods[index].endTime
                  setBlockPeriods((prev) => {
                    const newPeriods = [...prev]
                    newPeriods[index] = {
                      ...newPeriods[index],
                      startTime: value || '09:00',
                      endTime,
                    }
                    return newPeriods
                  })
                }}
              />
              <Text className="text-[#9AA0A8]">-</Text>
              <Select
                name={`block-end-${index}`}
                data={timeOptions.map((opt) => ({
                  ...opt,
                  disabled:
                    period.endDate.toDateString() === period.startDate.toDateString() &&
                    opt.value <= period.startTime, // do not allow end time before start time on the same day
                }))}
                rightSectionWidth={0}
                value={period.endTime}
                size="sm"
                withCheckIcon={false}
                classNames={{
                  input: `${timeInputClass} max-w-[96px] text-center !p-2`,
                  option: '!text-[13px] py-[0px] text-center ',
                }}
                comboboxProps={{ withinPortal: true }}
                allowDeselect={false}
                onChange={(value) => {
                  setBlockPeriods((prev) => {
                    const newPeriods = [...prev]
                    newPeriods[index] = { ...newPeriods[index], endTime: value || '17:00' }
                    return newPeriods
                  })
                }}
              />
              {isMultipleDayRange(period.startDate.toISOString(), period.endDate.toISOString()) && (
                <Tooltip
                  label={
                    <>
                      <span>When a range covers multiple days,</span> <br /> the system
                      automatically creates a time slot <br /> for each day using the chosen start
                      and end times.
                    </>
                  }
                  color="#316DAB"
                  className="cursor-help"
                >
                  <span className="flex h-9 items-center">
                    <IconInfoTriangleFilled color="orange" size={30} />
                  </span>
                </Tooltip>
              )}
              <button
                type="button"
                title="Remove"
                onClick={() => removePeriod(index)}
                disabled={blockPeriods.length <= 1}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#D8DBE0] text-[#8A929C] transition-colors hover:bg-[#F2F4F6] hover:text-[#1F2733] disabled:opacity-30"
              >
                <IconX size={16} stroke={2} />
              </button>
            </div>
          ))}
          {/* Footer */}
          <div className="mt-6 flex items-center justify-end gap-2.5 border-t border-[#EEF0F3] pt-[18px]">
            <button
              type="button"
              onClick={addNewPeriod}
              className="schedule-v2-mulish mr-auto inline-flex items-center gap-2 rounded-md px-2 py-1 text-[13px] font-bold text-[#E5533D] hover:bg-[#FDECE8]"
            >
              Add
              <span className="flex h-5 w-5 items-center justify-center rounded-full border border-[#E5533D]">
                <IconPlus size={11} stroke={2} />
              </span>
            </button>
            <button type="button" onClick={onClose} className={cancelBtn}>
              Cancel
            </button>
            <button
              type="button"
              disabled={Object.keys(scheduleDraft).length === 0 || invalidBlockKeys.size > 0}
              onClick={handleSave}
              className={saveBtn(
                Object.keys(scheduleDraft).length === 0 || invalidBlockKeys.size > 0,
              )}
            >
              Save
            </button>
          </div>
        </div>
      )}
    </Modal>
  )
}
